import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Pagination, { usePagination } from '../../../components/ui/Pagination';
import SortableTh, { useSort } from '../../../components/ui/SortableTh';
import useTableFilters from '../../../components/ui/useTableFilters';
import Icon from '../../../components/AppIcon';
import { adminService } from '../../../services/adminService';
import { formatDate } from '../../../utils/formatters';
import { conferenceStatusOptions } from '../../../constants/conferenceStatus';

const ConferenceTable = ({ conferences, selectedConferences, onSelectAll, onSelectConference, onRefresh }) => {
  // Table-local sorting: this component owns the rows it renders.
  const { sort, toggle, sortRows } = useSort('startDate', 'desc');
  const f = useTableFilters(conferences, React.useMemo(() => ({
    organizer: { label: 'Organizer', accessor: (c) => c?.organizer?.name || c?.organizerName || '', emptyLabel: 'No organizer' },
    status:    { label: 'Status',    accessor: (c) => c?.status || '',
      format: (v) => String(v).replace(/_/g,' ').replace(/^./, (x) => x.toUpperCase()) },
  }), []));
  const SORT_ACCESSORS = {
    organizer: (r) => r?.organizerName || r?.createdByName || '',
  };
  const sortedRows = sortRows(f.filterRows(conferences), SORT_ACCESSORS);
  // Paginate after sorting, so page 1 is the top of the sorted list
  // rather than the top of an arbitrary slice.
  const { pageRows, ...pager } = usePagination(sortedRows, 25);
  const [expandedRow, setExpandedRow] = useState(null);
  const [error, setError] = useState('');

  const formatDateSafe = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString) || 'Invalid date';
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-muted text-muted-foreground',
      published: 'bg-primary/10 text-primary',
      submission_open: 'bg-success/10 text-success',
      submission_closed: 'bg-warning/10 text-warning',
      review_phase: 'bg-secondary/10 text-secondary',
      completed: 'bg-muted text-muted-foreground',
      cancelled: 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const handleStatusChange = async (conferenceId, newStatus) => {
    try {
      await adminService?.updateConferenceStatus(conferenceId, newStatus);
      onRefresh();
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (conferenceId) => {
    if (!window.confirm('Delete this conference? This action cannot be undone.')) {
      return;
    }

    try {
      await adminService?.deleteConference(conferenceId);
      onRefresh();
    } catch (err) {
      setError(err?.message || 'Failed to delete conference');
    }
  };

  return (
    <div>
      {error && (
        <div className="m-4 p-4 bg-error/10 border-2 border-error rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="table-clear">
          <thead>
            <tr>
              <th className="!w-10">
                <input
                  type="checkbox"
                  checked={selectedConferences?.length === conferences?.length}
                  onChange={onSelectAll}
                  className="w-4 h-4 border-border"
                />
              </th>
              <SortableTh field="name" sort={sort} onSort={toggle}>Conference</SortableTh>
              <SortableTh field="organizer" sort={sort} onSort={toggle} filter={f.headerProps('organizer')}>Organizer</SortableTh>
              <th>Dates</th>
              <SortableTh field="status" sort={sort} onSort={toggle} filter={f.headerProps('status')}>Status</SortableTh>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows?.map((conf) => (
              <React.Fragment key={conf?.id}>
                <tr>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedConferences?.includes(conf?.id)}
                      onChange={() => onSelectConference(conf?.id)}
                      className="w-4 h-4 border-border"
                    />
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedRow(expandedRow === conf?.id ? null : conf?.id)}
                        className="p-1 hover:bg-muted transition-colors"
                      >
                        <Icon
                          name={expandedRow === conf?.id ? 'ChevronDown' : 'ChevronRight'}
                          size={16}
                          className="text-muted-foreground"
                        />
                      </button>
                      <div>
                        <div className="font-medium text-foreground">{conf?.name}</div>
                        <div className="text-xs text-muted-foreground">{conf?.shortName}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="text-foreground">{conf?.organizerName}</div>
                      <div className="text-xs text-muted-foreground">{conf?.organizerEmail}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="text-foreground">{formatDate(conf?.startDate)}</div>
                      <div className="text-xs text-muted-foreground">to {formatDate(conf?.endDate)}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(conf?.status)}`}>
                      {conf?.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Select
                        value={conf?.status}
                        onChange={(value) => handleStatusChange(conf?.id, value)}
                        className="min-w-[11rem]"
                        options={conferenceStatusOptions()}
                      />
                      <button
                        onClick={() => handleDelete(conf?.id)}
                        className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === conf?.id && (
                  <tr className="bg-muted/30">
                    <td colSpan="6" className="!py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">Conference Details</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-muted-foreground">
                              <span className="font-medium">Paper Deadline:</span> {formatDate(conf?.paperDeadline)}
                            </p>
                            <p className="text-muted-foreground">
                              <span className="font-medium">Created:</span> {formatDate(conf?.createdAt)}
                            </p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">Organizer Contact</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-muted-foreground">
                              <span className="font-medium">Name:</span> {conf?.organizerName}
                            </p>
                            <p className="text-muted-foreground">
                              <span className="font-medium">Email:</span> {conf?.organizerEmail}
                            </p>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pager} />
    </div>
  );
};

export default ConferenceTable;